import { computed, ref } from 'vue';
import { store } from '@/appContext';
import RegistrationClient from '@/api/RegistrationClient';
import DocusignCollectionMethod from '@/constants/docusignCollectionMethod';

export const DOCUSIGN_STATUS_POLL_INTERVAL_MS = 15000;

const docusignStatus = ref(null);
const docusignStatusLoading = ref(true);
const acvRepInfo = ref({ name: '', email: '' });
let docusignStatusPollIntervalId = null;

/**
 * Provides access to the currently selected dealer in the registration flow.
 *
 * Wraps the `selectedDealer` and `organizationAcvId` Vuex getters and their update actions so
 * components don't need to know the store module structure. `organizationAcvId` is the
 * dealer/org id the step-up endpoint requires (`useRegistrationStepUp`); it's stored here
 * rather than passed between call sites since it's produced once (from `registerDealership`'s
 * response) but needed by every later step-up call in the flow.
 *
 * @returns {{
 *   acvRepInfo: import('vue').Ref<{name: string, email: string}>,
 *   clearSelectedDealer: () => void,
 *   dealerEmail: import('vue').ComputedRef<string|null>,
 *   docusignStatus: import('vue').Ref<Object|null>,
 *   docusignStatusLoading: import('vue').Ref<boolean>,
 *   fetchAcvRepInfo: () => Promise<Object>,
 *   fetchDocusignStatus: () => Promise<Object>,
 *   hasLoadedDocusignStatus: import('vue').ComputedRef<boolean>,
 *   hasSelectedDealerName: import('vue').ComputedRef<boolean>,
 *   isDocusignCollection: import('vue').ComputedRef<boolean>,
 *   lpoaComplete: import('vue').ComputedRef<boolean>,
 *   organizationAcvId: import('vue').ComputedRef<string|null>,
 *   registeringDealerId: import('vue').ComputedRef<number|null>,
 *   registrationComplete: import('vue').ComputedRef<boolean>,
 *   registrationSpecialistName: import('vue').ComputedRef<string|null>,
 *   selectedDealer: import('vue').ComputedRef<Object|null>,
 *   selectedDealerName: import('vue').ComputedRef<string>,
 *   selectedDealerShortState: import('vue').ComputedRef<string>,
 *   selectedDealerState: import('vue').ComputedRef<string>,
 *   setOrganizationAcvId: (value: string) => void,
 *   setSelectedDealer: (dealer: Object) => void,
 *   startDocusignStatusPolling: () => void,
 *   stopDocusignStatusPolling: () => void,
 *   taxResaleCollectionMethod: import('vue').ComputedRef<string|null>,
 *   taxResaleComplete: import('vue').ComputedRef<boolean>,
 *   taxResaleRequired: import('vue').ComputedRef<boolean>,
 *   taxResaleSigned: import('vue').ComputedRef<boolean|null>,
 * }}
 */
export default function useRegistrationDealer() {
  const selectedDealer = computed(() => store.getters.selectedDealer);

  const selectedDealerName = computed(() => (
    selectedDealer.value !== null && selectedDealer.value.name !== undefined
      ? selectedDealer.value.name.toLowerCase()
      : ''
  ));

  const selectedDealerState = computed(() => (
    selectedDealer.value !== null && selectedDealer.value.address !== undefined
      ? selectedDealer.value.address.state
      : ''
  ));

  const selectedDealerShortState = computed(() => (
    selectedDealer.value !== null && selectedDealer.value.address !== undefined
      ? selectedDealer.value.address.shortState
      : ''
  ));

  const hasLoadedDocusignStatus = computed(() => docusignStatus.value !== null);

  const registeringDealerId = computed(() => docusignStatus.value?.dealershipId ?? null);

  const lpoaComplete = computed(() => docusignStatus.value?.lpoaSigned === true);

  const taxResaleRequired = computed(() => docusignStatus.value?.taxResale?.required === true);

  const taxResaleCollectionMethod = computed(
    () => docusignStatus.value?.taxResale?.collectionMethod ?? null,
  );

  const isDocusignCollection = computed(
    () => taxResaleCollectionMethod.value === DocusignCollectionMethod.DOCUSIGN,
  );

  const taxResaleSigned = computed(() => docusignStatus.value?.taxResale?.signed ?? false);

  const registrationSpecialistName = computed(
    () => docusignStatus.value?.registrationSpecialistName ?? null,
  );

  const taxResaleComplete = computed(
    () => !taxResaleRequired.value || taxResaleSigned.value === true,
  );

  const registrationComplete = computed(
    () => lpoaComplete.value === true && taxResaleComplete.value === true,
  );

  const hasSelectedDealerName = computed(
    () => selectedDealerName.value !== undefined && selectedDealerName.value.length > 0,
  );

  const fetchAcvRepInfo = async () => {
    const client = new RegistrationClient();
    const response = await client.getDealerAssignedRep();
    if (response.status === 200) {
      acvRepInfo.value = response.data;
    }
    return response;
  };

  const dealerEmail = computed(() => docusignStatus.value?.userEmail ?? null);

  const organizationAcvId = computed(() => store.getters.organizationAcvId);

  const setOrganizationAcvId = (value) => store.dispatch('updateOrganizationAcvId', value);

  const fetchDocusignStatus = async () => {
    const client = new RegistrationClient();
    const response = await client.getDocusignStatus();
    if (response.status === 200) {
      docusignStatus.value = response.data;
    }
    docusignStatusLoading.value = false;
    return response;
  };

  const stopDocusignStatusPolling = () => {
    if (docusignStatusPollIntervalId === null) {
      return;
    }
    clearInterval(docusignStatusPollIntervalId);
    docusignStatusPollIntervalId = null;
  };

  const startDocusignStatusPolling = () => {
    if (docusignStatusPollIntervalId !== null) {
      return;
    }
    const pollOnce = async () => {
      await fetchDocusignStatus();
      if (registrationComplete.value) {
        stopDocusignStatusPolling();
      }
    };
    docusignStatusPollIntervalId = setInterval(pollOnce, DOCUSIGN_STATUS_POLL_INTERVAL_MS);
    pollOnce();
  };

  const resetDocusignStatus = () => {
    stopDocusignStatusPolling();
    docusignStatus.value = null;
    docusignStatusLoading.value = true;
  };

  const setSelectedDealer = (dealer) => {
    resetDocusignStatus();
    store.dispatch('updateSelectedDealer', dealer);
  };

  const clearSelectedDealer = () => {
    resetDocusignStatus();
    store.dispatch('updateSelectedDealer', null);
  };

  return {
    acvRepInfo,
    clearSelectedDealer,
    dealerEmail,
    docusignStatus,
    docusignStatusLoading,
    fetchAcvRepInfo,
    fetchDocusignStatus,
    hasLoadedDocusignStatus,
    hasSelectedDealerName,
    isDocusignCollection,
    lpoaComplete,
    organizationAcvId,
    registeringDealerId,
    registrationComplete,
    registrationSpecialistName,
    selectedDealer,
    selectedDealerName,
    selectedDealerShortState,
    selectedDealerState,
    setOrganizationAcvId,
    setSelectedDealer,
    startDocusignStatusPolling,
    stopDocusignStatusPolling,
    taxResaleCollectionMethod,
    taxResaleComplete,
    taxResaleRequired,
    taxResaleSigned,
  };
}
